import { Header } from "@/components/Header";
import { ShieldCheck, MessageSquare, Heart, Star } from "lucide-react";
import { useTestimonials } from "@/hooks/useTestimonials";
import { useMemo } from "react";
import { HomepagePreview } from "@/components/HomepagePreview";
import { Footer } from "@/components/Footer";
import { SmartImage } from "@/components/SmartImage";
import { SeoHead } from "@/components/SeoHead";
import { LocalBusinessStructuredData } from "@/components/StructuredData";
import { Helmet } from "react-helmet-async";
import { useSiteImages } from "@/hooks/useSiteImages";

const STATIC_HERO = "/hero-home.webp";

const Index = () => {
  const { images: siteImages } = useSiteImages();
  const { testimonials } = useTestimonials(false);
  const ratingStats = useMemo(() => {
    if (!testimonials.length) return { avg: 0, count: 0 };
    const sum = testimonials.reduce((s, t) => s + t.rating, 0);
    return { avg: sum / testimonials.length, count: testimonials.length };
  }, [testimonials]);
  const heroEntry = siteImages['hero_home'];
  const heroSrc = heroEntry?.image_url || STATIC_HERO;
  const heroFocal = (heroEntry as any)?.focal_point ?? null;
  const scrollToGuide = () => {
    const el = document.getElementById('presentation-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      <SeoHead
        title="Excursions Rio de Janeiro | Guide Francophone Certifiée · Roca Rio"
        description="Excursions à Rio de Janeiro avec une guide française certifiée. Randonnées, favelas, spots secrets en petit groupe. Tourisme authentique et solidaire. Réponse WhatsApp sous 24h."
        canonical="https://rocario-cleacarioca.com/"
        keywords="excursions Rio de Janeiro, guide francophone Rio, tourisme solidaire Brésil, randonnées Rio, visite Rio, Pedra da Gávea, Corcovado, favelas Rio"
        ogImage={heroSrc}
        ogImageAlt="Vue panoramique de Rio de Janeiro avec le Pain de Sucre et le Corcovado"
      />
      {/* Préchargement LCP : le navigateur récupère l'image du hero immédiatement */}
      <Helmet>
        <link rel="preload" as="image" href={heroSrc} {...({ fetchpriority: 'high' } as any)} />
      </Helmet>

      <LocalBusinessStructuredData type="TravelAgency" />

      <Header hideOnHero />

      <main className="bg-background">
        {/* Hero · structure éditoriale, photo entièrement visible */}
        <section className="relative h-[100svh] min-h-[600px] flex items-end overflow-hidden pb-[max(5rem,env(safe-area-inset-bottom))] sm:pb-24">
          <div className="absolute inset-0">
            <SmartImage
              src={heroSrc}
              alt="Vue panoramique de Rio de Janeiro avec le Pain de Sucre et le Corcovado - Roca Rio"
              aspectClassName="h-full w-full"
              className="object-cover object-left sm:object-bottom"
              loading="eager"
              fetchPriority="high"
              sizes="100vw"
              focalPoint={heroFocal}
            />
          </div>

          {/* Dégradé harmonisé avec À propos / Blog */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70 pointer-events-none" style={{ zIndex: 1 }} />

          <div className="relative w-full container mx-auto px-4 sm:px-6 lg:px-8" style={{ zIndex: 2 }}>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 lg:gap-12">
              <div className="lg:max-w-[60%] animate-fade-in">
                <p className="text-xs sm:text-base uppercase tracking-[0.25em] sm:tracking-[0.3em] text-white/70 mb-2 sm:mb-3 font-semibold font-montserrat">
                  par Cléa Carioca
                </p>
                <p className="text-[clamp(2.75rem,14vw,6rem)] font-league uppercase text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)] leading-[0.9] animate-blur-in" aria-hidden="true">
                  Roca Rio
                </p>
                <h1 className="sr-only">Excursions Rio de Janeiro · Guide Francophone Certifiée Roca Rio</h1>
              </div>

              <div className="w-full lg:max-w-sm space-y-4 sm:space-y-5 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="w-12 h-px bg-roca-orange hidden lg:block" />
                <p className="text-base sm:text-xl text-white/90 leading-relaxed drop-shadow-lg font-montserrat">
                  Vivez Rio autrement&nbsp;: plus local, plus humain, plus engagé.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="/contact"
                    className="inline-flex w-full sm:w-auto justify-center items-center gap-3 px-6 sm:px-7 py-3 sm:py-3.5 min-h-11 bg-roca-orange text-white font-semibold font-montserrat rounded-lg hover:bg-roca-orange/90 hover:scale-[1.02] transition-all shadow-lg uppercase tracking-wide text-sm"
                  >
                    Réserver mon excursion
                  </a>
                  <button
                    onClick={scrollToGuide}
                    className="inline-flex w-full sm:w-auto justify-center items-center gap-3 px-6 sm:px-7 py-3 sm:py-3.5 min-h-11 bg-white/10 backdrop-blur-sm border border-white/40 text-white font-semibold font-montserrat rounded-lg hover:bg-white/20 transition-all uppercase tracking-wide text-sm"
                  >
                    Découvrir
                  </button>
                </div>
                <ul className="grid grid-cols-2 sm:flex sm:flex-wrap gap-x-4 sm:gap-x-5 gap-y-2 pt-1 text-[10px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.15em] text-white/80 font-montserrat">
                  {ratingStats.count > 0 && (
                    <li className="flex items-center gap-1.5">
                      <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-roca-orange text-roca-orange flex-shrink-0" />
                      <span className="font-semibold text-white">{ratingStats.avg.toFixed(1)}/5</span>
                      <span className="text-white/70 truncate">· {ratingStats.count} avis</span>
                    </li>
                  )}
                  <li className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-roca-orange flex-shrink-0" />
                    Guide certifiée
                  </li>
                  <li className="flex items-center gap-1.5">
                    <MessageSquare className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-roca-orange flex-shrink-0" />
                    Réponse sous 24h
                  </li>
                  <li className="flex items-center gap-1.5">
                    <Heart className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-roca-orange flex-shrink-0" />
                    Tourisme solidaire
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <HomepagePreview />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
